import type { Extension, Resource } from '@medplum/fhirtypes';

import { ELUVE_FHIR_NAMESPACE } from '@eluve/fhir-utils';

/**
 * The clinician's per-finding answer to "what does this fact mean for the
 * plan": `plan` (it joined the care plan) or `record` (deliberately kept on the
 * patient's record only).
 *
 * ABSENCE of the marker is load-bearing: it means the link question was never
 * answered, which is what lets a reload distinguish an unreviewed finding from
 * a deliberately-unlinked one — both otherwise read as approved-and-unreferenced.
 */
export const LINK_DECISION_EXTENSION = `${ELUVE_FHIR_NAMESPACE}/StructureDefinition/link-decision`;

export type LinkDecision = {
  decision: 'plan' | 'record';
  /** Absent when no plan existed to decide against. */
  carePlanRef?: string;
  recordedAt: string;
};

/** The link decision recorded on a resource, or null when never answered. */
export function readLinkDecision(resource: Resource): LinkDecision | null {
  const marker = (resource as { extension?: Extension[] }).extension?.find(
    (e) => e.url === LINK_DECISION_EXTENSION,
  );
  const parts = marker?.extension;
  if (!parts) {
    return null;
  }

  const decision = parts.find((e) => e.url === 'decision')?.valueCode;
  if (decision !== 'plan' && decision !== 'record') {
    return null;
  }

  const carePlanRef = parts.find((e) => e.url === 'carePlan')?.valueReference
    ?.reference;
  const recordedAt =
    parts.find((e) => e.url === 'recordedAt')?.valueDateTime ?? '';

  return {
    decision,
    ...(carePlanRef ? { carePlanRef } : {}),
    recordedAt,
  };
}

/** Drop the marker, returning the resource to "the link question was never
 *  answered" — for rewinds (undo, dismiss) where a stamped decision would
 *  otherwise outlive the review state it was made in. */
export function withoutLinkDecision<T extends Resource>(resource: T): T {
  const extension = (resource as { extension?: Extension[] }).extension;
  if (!extension) return resource;
  return {
    ...resource,
    extension: extension.filter((e) => e.url !== LINK_DECISION_EXTENSION),
  };
}

/** Record the link decision on a resource — re-deciding replaces the prior
 *  marker, it never stacks a second one. */
export function withLinkDecision<T extends Resource>(
  resource: T,
  decision: LinkDecision,
): T {
  const others = (
    (resource as { extension?: Extension[] }).extension ?? []
  ).filter((e) => e.url !== LINK_DECISION_EXTENSION);

  return {
    ...resource,
    extension: [
      ...others,
      {
        url: LINK_DECISION_EXTENSION,
        extension: [
          { url: 'decision', valueCode: decision.decision },
          ...(decision.carePlanRef
            ? [
                {
                  url: 'carePlan',
                  valueReference: { reference: decision.carePlanRef },
                },
              ]
            : []),
          { url: 'recordedAt', valueDateTime: decision.recordedAt },
        ],
      },
    ],
  };
}
